import { useEffect } from "react";
import { motion } from "framer-motion";
import { BarChart3, Workflow, BrainCircuit, Bot, Handshake, ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import SEO, { buildBreadcrumbJsonLd } from "@/components/SEO";
import { seoConfig } from "@/data/seoConfig";

const dataServices = [
  {
    icon: BarChart3,
    title: "Data Visualization",
    desc: "Utilize our advanced Business Intelligence tools and services to visualize your data. We create intuitive dashboards and reports that provide clear insights and support data-driven decision-making.",
  },
  {
    icon: Workflow,
    title: "Data Engineering",
    desc: "Our data engineering services consolidate your data sources into a single, reliable source of truth. Focus on strategy and decision-making, not on data wrangling.",
  },
];

const aiCards = [
  {
    icon: BrainCircuit,
    title: "Data Science and RPA",
    desc: "Leverage your data investments with predictive analytics to foresee business trends and automate redundant processes. Our expertise in Data Science and Robotic Process Automation (RPA) helps expedite operations and reduce errors.",
    badge: "Predictive Analysis and Automation",
  },
  {
    icon: Bot,
    title: "Machine Learning Development",
    desc: "Develop and implement cutting-edge Machine Learning models tailored to your business needs. Our team stays ahead of industry trends to deliver innovative and effective AI solutions.",
    badge: "Advanced AI Solutions",
  },
  {
    icon: Workflow,
    title: "Robotic Process Automation (RPA)",
    desc: "Automate mundane and repetitive tasks with our RPA services, drastically reducing errors and increasing operational efficiency. Our RPA solutions are designed to optimize your business processes.",
    badge: "Streamline and Automate Operations",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const DataIntelligence = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO {...seoConfig.dataIntelligence} jsonLd={[buildBreadcrumbJsonLd(seoConfig.dataIntelligence.crumbs)]} />
      <PageHero
        title="Data and Intelligence"
        crumbs={[
          { label: "Home", href: "/" },
          { label: "Technology Services", href: "/services/technology-services" },
          { label: "Data and Intelligence" },
        ]}
        lightfall
      />

      <section data-testid="data-intro" className="pt-24 lg:pt-12 pb-24 lg:pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10">
              <img
                src="/assets/Data_Analytics.jpg"
                alt="Data Analytics"
                className="w-full h-[400px] lg:h-[560px] object-cover"
              />
            </div>
          </motion.div>
          <div>
            <motion.div {...fadeUp}>
              <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">Data Services</span>
              <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl text-slate-900 leading-tight">
                Comprehensive Data Management
              </h2>
              <p className="mt-6 text-base md:text-lg text-slate-500 leading-relaxed">
                We curate data from disparate sources to provide a unified, accurate view of your business. Our
                services ensure that your data is organized, accessible, and ready for analysis.
              </p>
            </motion.div>
            <div className="mt-9 space-y-6">
              {dataServices.map((s, i) => (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, x: 24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.55, delay: i * 0.1 }}
                  className="flex items-start gap-5"
                  data-testid={`data-service-${i + 1}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-miti-blue/10 text-miti-blue flex items-center justify-center shrink-0">
                    <s.icon size={22} strokeWidth={1.8} />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-lg text-slate-900">{s.title}</h3>
                    <p className="mt-1.5 text-sm md:text-base text-slate-500 leading-relaxed">{s.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section data-testid="data-expertise" className="relative py-28 lg:py-36 overflow-hidden">
        <img
          src="/assets/Data.jpg"
          alt="Unlock the Full Potential of Your Data"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-miti-navy/75" />
        <motion.div {...fadeUp} className="relative max-w-3xl mx-auto px-6 text-center">
          <span className="text-xs font-semibold tracking-[0.25em] uppercase text-sky-300">
            Transform Data into Strategic Insights and Action
          </span>
          <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white">
            Unlock the Full Potential of Your Data
          </h2>
          <div className="mt-5 mx-auto h-0.5 w-12 bg-miti-blue" />
          <p className="mt-6 text-base md:text-lg text-slate-200 leading-relaxed">
            We specialize in integrating cloud technology, data management, and process automation to deliver
            robust, scalable solutions. Our technical prowess ensures your business remains competitive and agile.
          </p>
        </motion.div>
      </section>

      <section data-testid="data-ai-cards" className="py-24 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-5">
          {aiCards.map((c, i) => (
            <motion.div
              key={c.title}
              {...fadeUp}
              transition={{ duration: 0.65, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -6 }}
              data-testid={`data-ai-card-${i + 1}`}
              className="relative rounded-2xl border border-slate-200 bg-white p-8 pt-10 hover:border-miti-blue/40 hover:shadow-xl hover:shadow-miti-blue/5 transition-all duration-300"
            >
              <span className="absolute top-4 right-4 rounded-full bg-miti-blue/10 text-miti-blue px-3 py-1 text-[10px] font-bold tracking-wider uppercase">
                {c.badge}
              </span>
              <div className="w-12 h-12 rounded-xl bg-miti-blue/10 text-miti-blue flex items-center justify-center">
                <c.icon size={22} strokeWidth={1.8} />
              </div>
              <h3 className="mt-5 font-heading font-semibold text-lg text-slate-900 leading-snug">{c.title}</h3>
              <p className="mt-3 text-sm md:text-base text-slate-500 leading-relaxed">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section data-testid="data-cta" className="py-24 lg:py-16 bg-slate-50/70">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <motion.div {...fadeUp}>
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">
              Start a Conversation
            </span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">
              Ready to Transform Your Data Strategy?
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-500 leading-relaxed">
              Connect with us today to explore how our Data and Intelligence services can elevate your business.
              Let's turn your data into a strategic asset.
            </p>
            <a
              href="/contact"
              data-testid="data-contact-button"
              className="group mt-9 inline-flex items-center gap-3 rounded-lg bg-miti-blue px-7 py-3.5 text-sm font-semibold text-white hover:bg-[#005a96] transition-colors"
            >
              <Handshake size={18} />
              Contact Us
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10">
              <img
                src="/assets/cta-bg.jpg"
                alt="Start a Conversation"
                className="w-full h-[320px] lg:h-[420px] object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default DataIntelligence;
